<!-- ROO: Bind internal _value and multiple to mat-select -->
<mat-select
  #matSelect
  [disabled]="disabled"
  [placeholder]="placeholder"
  [required]="required"
  [compareWith]="compareWith"
  [sortComparator]="sortComparator"
  [disableOptionCentering]="disableOptionCentering"
  [disableRipple]="disableRipple"
  [errorStateMatcher]="errorStateMatcher"
  [panelClass]="getPanelClass | call : panelClass"
  (openedChange)="onOpenedChange($event)"
  (keydown)="handleSelectKeydown($event)"
  value="dummy"
>
  <!-- ROO: Trigger remains the same -->
  <mat-select-trigger>
    <ng-container
      *ngTemplateOutlet="
        selectLabelDirective?.template || tmplDefaultLabel;
        context: getTriggerContext | call : valueMap
      "
    ></ng-container>
  </mat-select-trigger>

  <!-- ROO: Dummy option might still be needed for mat-select internals -->
  <mat-option value="dummy" style="display: none"></mat-option>

  <!-- ROO: Search Box (now potentially inside tabs or above) -->
  <div class="{{ controlType }}-header">
    <div class="{{ controlType }}-search-box" *ngIf="searchable">
      <mat-pseudo-checkbox
        ngClass="{{ controlType }}-master-checkbox {{ controlType }}-item-checkbox"
        *ngIf="multiple && showMasterCheckbox"
        [state]="getMasterChecked | call : (filteredItems$ | async) : valueMap"
        (click)="$event.stopPropagation(); toggleMasterCheckbox()"
      >
      </mat-pseudo-checkbox>

      <input
        #searchInput
        class="{{ controlType }}-input"
        [formControl]="searchControl"
        [placeholder]="searchPlaceholder"
        (keydown)="handleInputKeydown($event)"
      />
      <div class="{{ controlType }}-buttons">
        <button
          type="button"
          mat-icon-button
          class="{{ controlType }}-button {{ controlType }}-button-clear"
          *ngIf="searchControl.value"
          (click)="$event.stopPropagation(); clearSearch(); searchInput.focus()"
          aria-label="Clear search"
        >
          <i class="far fa-times"></i>
        </button>

        <button
          type="button"
          mat-button
          class="{{ controlType }}-button {{ controlType }}-button-close"
          *ngIf="showCloseButton"
          (click)="$event.stopPropagation(); close()"
          i18n
        >
          Close
        </button>

        <ng-content select="[select-top-buttons]"></ng-content>
      </div>
    </div>

    <ng-content select="[select-top-content]"></ng-content>
  </div>

  <!-- ROO: Tab Group for Grouped Items -->
  <mat-tab-group
    class="{{ controlType }}-tab-group"
    *ngIf="groupBy && groupNames.length > 1; else noTabs"
    animationDuration="0ms"
    [selectedIndex]="selectedGroupIndex$ | async"
    (selectedIndexChange)="selectGroup($event)"
    (keydown)="$event.stopPropagation()"
  >
    <mat-tab *ngFor="let groupName of groupNames; let i = index" [label]="groupName">
      <!-- Content for each tab (virtual scroll) -->
      <ng-container
        *ngTemplateOutlet="
          itemsList;
          context: { $implicit: (filteredItems$ | async), focusedItemIndex: (focusedItemIndex$ | async) }
        "
      ></ng-container>
    </mat-tab>
  </mat-tab-group>

  <!-- ROO: Render items directly if not grouping -->
  <ng-template #noTabs>
    <ng-container
      *ngTemplateOutlet="
        itemsList;
        context: { $implicit: (filteredItems$ | async), focusedItemIndex: (focusedItemIndex$ | async) }
      "
    ></ng-container>
  </ng-template>

  <!-- Items List Template (reusable for tabs and no-tabs) -->
  <ng-template #itemsList let-filteredItems let-focusedItemIndex="focusedItemIndex">
    <!-- Focus trap needs to be inside the scrollable area or tab content -->
    <input readonly class="{{ controlType }}-focus-trap" (keydown)="handleItemFocusTrapKeydown($event)" />

    <div
      class="{{ controlType }}-items"
      [ngStyle]="{ 'height.px': (getScrollHeight | call : $safeNavigationMigration(filteredItems?.length)) }"
    >
      <!-- Widest item placeholder (optional, consider if needed with tabs) -->
      <div
        *ngIf="widestItem$ | async; let widestItem"
        class="{{ controlType }}-item mat-option {{ controlType }}-widest-item-placeholder"
        [ngClass]="{ 'mat-option-multiple': multiple }"
        aria-hidden="true"
      >
        <mat-pseudo-checkbox ngClass="{{ controlType }}-item-checkbox" *ngIf="multiple"></mat-pseudo-checkbox>
        <div class="{{ controlType }}-item-content">{{ getLabel | call : widestItem }}</div>
      </div>

      <!-- No items message -->
      <div *ngIf="!filteredItems?.length" class="mat-option mat-option-disabled {{ controlType }}-no-items">
        {{ _items?.length ? textNoMatch : textNoItems }}
      </div>

      <!-- Virtual Scroll Viewport -->
      <cdk-virtual-scroll-viewport
        #scroller
        *ngIf="filteredItems?.length"
        class="{{ controlType }}-scroller"
        [itemSize]="itemHeight"
        orientation="vertical"
      >
        <div
          #elmItem
          *cdkVirtualFor="
            let item of filteredItems;
            let index = index;
            let first = first;
            let last = last;
            let even = even;
            let odd = odd;
            trackBy: trackByValue
          "
          class="{{ controlType }}-item mat-option"
          role="option"
          [id]="id + '-option-' + (getValue | call : item)"
          [attr.aria-selected]="(getItemChecked | call : item : valueMap) === 'checked'"
          [ngClass]="{
            'mat-option-multiple': multiple,
            'mat-selected': (getItemChecked | call : item : valueMap) === 'checked',
            'mat-active': index === focusedItemIndex
          }"
          [ngStyle]="{ 'height.px': itemHeight }"
          (click)="$event.stopPropagation(); onItemClick(item)"
          [class.disabled]="!isItemSelectable(item)"
          [matTooltip]="getItemTooltip(item)"
          [matTooltipPosition]="tooltipPosition"
        >
          <mat-pseudo-checkbox
            ngClass="{{ controlType }}-item-checkbox"
            *ngIf="multiple"
            [state]="getItemChecked | call : item : valueMap"
          >
          </mat-pseudo-checkbox>

          <!-- Blank Item Content -->
          <div class="{{ controlType }}-item-content" *ngIf="item === blankValue">
            <div class="{{ controlType }}-item-content-inner">
              {{ blankLabel }}
            </div>
          </div>

          <!-- Regular Item Content -->
          <div class="{{ controlType }}-item-content" *ngIf="item !== blankValue">
            <div class="{{ controlType }}-item-content-inner">
              <ng-container
                *ngTemplateOutlet="
                  selectItemDirective?.template || tmplDefaultItem;
                  context: {
                    $implicit: item,
                    item: item,
                    label: (getLabel | call : item),
                    value: (getValue | call : item),
                    index: index,
                    first: first,
                    last: last,
                    even: even,
                    odd: odd
                  }
                "
              ></ng-container>
            </div>
          </div>
        </div>
      </cdk-virtual-scroll-viewport>
    </div>
  </ng-template>

  <ng-content select="[select-bottom-content]"></ng-content>

  <!-- Action Button -->
  <div
    class="{{ controlType }}-action-button"
    *ngIf="selectActionButtonDirective?.template"
    (click)="$event.stopPropagation(); close()"
  >
    <ng-container
      *ngTemplateOutlet="
        $safeNavigationMigration(selectActionButtonDirective?.template);
        context: { searchText: searchControl.value, items: filteredItems$ | async }
      "
    ></ng-container>
  </div>
</mat-select>

<!-- Default Item Template -->
<ng-template #tmplDefaultItem let-label="label">
  <div class="{{ controlType }}-item-default-label" [title]="label">
    <div class="w-100 text-truncate">
      {{ label }}
    </div>
  </div>
</ng-template>

<!-- Default Label Template (Trigger) -->
<ng-template #tmplDefaultLabel let-items="items">
  {{ getTriggerValue | call : items }}
</ng-template>
